import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.hasAttribute('data-naturloom-gallery-polish')) {
      this.#initializeNaturloomGalleryPolish();
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#naturloomGalleryObserver?.disconnect();
    this.#naturloomResizeObserver?.disconnect();
  }

  #naturloomGalleryObserver;

  #naturloomResizeObserver;

  /**
   * Keeps the finite arrows, mobile height, counter, and thumbnail states aligned.
   */
  #initializeNaturloomGalleryPolish() {
    const slides = Array.from(this.querySelectorAll('slideshow-slide'));
    const previousButton = this.querySelector('slideshow-arrows [on\\:click="/previous"]');
    const nextButton = this.querySelector('slideshow-arrows [on\\:click="/next"]');

    previousButton?.setAttribute('aria-label', 'Previous product image');
    nextButton?.setAttribute('aria-label', 'Next product image');

    const sync = () => {
      const activeIndex = slides.findIndex((slide) => slide.getAttribute('aria-hidden') === 'false');
      const activeSlide = slides[activeIndex];

      if (!activeSlide) return;

      const productMedia = activeSlide.querySelector('.product-media');
      const ratio = Number.parseFloat(productMedia?.style.getPropertyValue('--ratio') || '1') || 1;
      const container = this.querySelector('slideshow-container');
      const availableWidth = container?.clientWidth || this.clientWidth;
      const cappedHeight = Math.min(availableWidth / ratio, window.innerHeight * 0.72);

      this.style.setProperty('--naturloom-gallery-height', `${Math.max(1, Math.round(cappedHeight))}px`);

      if (previousButton instanceof HTMLButtonElement) {
        previousButton.disabled = activeIndex <= 0;
      }

      if (nextButton instanceof HTMLButtonElement) {
        nextButton.disabled = activeIndex >= slides.length - 1;
      }

      const thumbnails = this.querySelectorAll('.slideshow-controls__thumbnail');
      thumbnails.forEach((thumbnail, index) => {
        const isCurrent = index % slides.length === activeIndex;
        thumbnail.setAttribute('aria-current', isCurrent ? 'true' : 'false');
        thumbnail.setAttribute('aria-selected', isCurrent ? 'true' : 'false');

        const thumbnailScroller = thumbnail.closest('.slideshow-controls__thumbnails-container');
        if (
          isCurrent &&
          thumbnail instanceof HTMLElement &&
          thumbnailScroller instanceof HTMLElement
        ) {
          const centeredLeft =
            thumbnail.offsetLeft - (thumbnailScroller.clientWidth - thumbnail.offsetWidth) / 2;
          thumbnailScroller.scrollTo({ left: Math.max(0, centeredLeft), behavior: 'auto' });
        }
      });
    };

    this.#naturloomGalleryObserver = new MutationObserver(sync);
    slides.forEach((slide) => {
      this.#naturloomGalleryObserver?.observe(slide, {
        attributes: true,
        attributeFilter: ['aria-hidden'],
      });
    });

    if ('ResizeObserver' in window) {
      this.#naturloomResizeObserver = new ResizeObserver(sync);
      this.#naturloomResizeObserver.observe(this);
    }

    window.addEventListener('resize', sync, { signal: this.#controller.signal, passive: true });
    requestAnimationFrame(sync);
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        const shouldRevealSelectedMedia =
          detail.explicitColorChange === true && Boolean(detail.resource?.featured_media?.id);
        this.replaceWith(newMediaGallery);

        const root = document.documentElement;
        const storedViewportScrollPosition = {
          left: Number(root.dataset.naturloomVariantScrollLeft),
          top: Number(root.dataset.naturloomVariantScrollTop),
        };
        const viewportScrollPosition =
          detail.viewportScrollPosition ??
          (Number.isFinite(storedViewportScrollPosition.left) && Number.isFinite(storedViewportScrollPosition.top)
            ? storedViewportScrollPosition
            : null);

        if (viewportScrollPosition) {
          const restoreScrollPosition = () =>
            window.scrollTo({ ...viewportScrollPosition, behavior: 'instant' });
          restoreScrollPosition();
          requestAnimationFrame(() => {
            restoreScrollPosition();
            requestAnimationFrame(restoreScrollPosition);
          });
          window.setTimeout(() => {
            restoreScrollPosition();
          }, 250);
          window.setTimeout(() => {
            restoreScrollPosition();
            requestAnimationFrame(() => {
              root.classList.remove('naturloom-variant-media-updating');
              restoreScrollPosition();
              requestAnimationFrame(restoreScrollPosition);
              delete root.dataset.naturloomVariantScrollLeft;
              delete root.dataset.naturloomVariantScrollTop;
            });
          }, 750);
        }

        if (shouldRevealSelectedMedia && newMediaGallery instanceof MediaGallery) {
          requestAnimationFrame(() => newMediaGallery.revealSelectedVariantMedia());
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Reveals the exact variant-linked media after an explicit customer color change.
   * The server-rendered gallery places that media first while preserving all other media order.
   */
  revealSelectedVariantMedia() {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (window.matchMedia('(max-width: 749px)').matches || this.presentation !== 'grid') {
      return;
    }

    const selectedMedia = this.media?.[0];
    if (!selectedMedia) return;

    const scrollContainer = this.#getScrollContainer();
    const usesDocumentScroll =
      scrollContainer === document.documentElement || scrollContainer === document.body;
    const scrollContainerRect = usesDocumentScroll
      ? { top: 0, bottom: window.innerHeight }
      : scrollContainer.getBoundingClientRect();
    const headerBottom = this.#getStickyHeaderBottom();
    const viewportTop = Math.max(scrollContainerRect.top, headerBottom) + 16;
    const viewportBottom = Math.min(scrollContainerRect.bottom, window.innerHeight);
    const mediaRect = selectedMedia.getBoundingClientRect();
    const visibleHeight = Math.max(
      0,
      Math.min(mediaRect.bottom, viewportBottom) - Math.max(mediaRect.top, viewportTop)
    );
    const visibleBasis = Math.max(1, Math.min(mediaRect.height, viewportBottom - viewportTop));

    if (visibleHeight / visibleBasis >= 0.6) return;

    const currentScroll = usesDocumentScroll ? window.scrollY : scrollContainer.scrollTop;
    const scrollOptions = {
      top: Math.max(0, currentScroll + mediaRect.top - viewportTop),
      behavior: reducedMotion ? 'auto' : 'smooth',
    };

    if (usesDocumentScroll) {
      window.scrollTo(scrollOptions);
    } else {
      scrollContainer.scrollTo(scrollOptions);
    }
  }

  /**
   * Finds the storefront's active scroll container.
   * @returns {Element}
   */
  #getScrollContainer() {
    let element = this.parentElement;

    while (element) {
      const styles = window.getComputedStyle(element);
      if (
        (styles.overflowY === 'auto' || styles.overflowY === 'scroll') &&
        element.scrollHeight > element.clientHeight
      ) {
        return element;
      }
      element = element.parentElement;
    }

    return document.scrollingElement || document.documentElement;
  }

  /**
   * Finds the bottom edge of any currently active sticky or fixed storefront header.
   * @returns {number}
   */
  #getStickyHeaderBottom() {
    let bottom = 0;

    for (const element of document.querySelectorAll('#header-component, .header, header')) {
      const styles = window.getComputedStyle(element);
      if (styles.position !== 'sticky' && styles.position !== 'fixed') continue;

      const rect = element.getBoundingClientRect();
      if (rect.top <= 1 && rect.bottom > 0) {
        bottom = Math.max(bottom, rect.bottom);
      }
    }

    return bottom;
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
